import React, { Children, cloneElement, isValidElement, useCallback, useEffect, useId, useMemo, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';

/**
 * Embla-backed drop-in replacement for `react-slick`'s `<Slider />`.
 *
 * The historical codebase passes slick-shaped config objects (`slidesToShow`,
 * `slidesToScroll`, `responsive`, `infinite`, `prevArrow`, `nextArrow`,
 * `dots`, `rows`, ...) into `<Slider {...settings}>`. Rather than refactor
 * every call site, this shim accepts the same shape and renders an embla
 * carousel while keeping slick's class names (`slick-list`, `slick-track`,
 * `slick-slide`, `slick-active`, `slick-dots`, `slick-disabled`) so the
 * existing SCSS continues to work unchanged.
 */

type ResponsiveBreakpoint = {
    breakpoint: number;
    settings: {
        slidesToShow?: number;
        slidesToScroll?: number;
        swipe?: boolean;
    };
};

export type SliderProps = {
    infinite?: boolean;
    speed?: number;
    slidesToShow?: number;
    slidesToScroll?: number;
    initialSlide?: number;
    dots?: boolean;
    swipe?: boolean;
    rows?: number;
    responsive?: ResponsiveBreakpoint[];
    nextArrow?: React.ReactElement;
    prevArrow?: React.ReactElement;
    className?: string;
    children?: React.ReactNode;
};

// Only drives embla's options. Starts unset on the client as on the server, so hydration
// matches; slide widths come from CSS (see layoutCss) and never wait for this.
const useResponsiveSettings = (breakpoints: ResponsiveBreakpoint[]) => {
    const sorted = useMemo(() => [...breakpoints].sort((a, b) => a.breakpoint - b.breakpoint), [breakpoints]);
    const [active, setActive] = useState<ResponsiveBreakpoint | undefined>(undefined);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const mqls = sorted.map((b) => ({ b, mql: window.matchMedia(`(max-width: ${b.breakpoint}px)`) }));
        const update = () => setActive(mqls.find(({ mql }) => mql.matches)?.b);
        update();
        mqls.forEach(({ mql }) => mql.addEventListener('change', update));
        return () => mqls.forEach(({ mql }) => mql.removeEventListener('change', update));
    }, [sorted]);

    return active;
};

/**
 * Slide widths per breakpoint as CSS, so the server HTML already has the right width for the
 * viewport. Mirrors the old JS lookup: the smallest matching `max-width` breakpoint wins, and a
 * breakpoint without `slidesToShow` falls back to the default.
 *
 * When `slideCount` is given, arrows also look disabled at breakpoints where every slide fits,
 * until embla has measured and set the real state (its `slick-disabled` class wins on specificity).
 */
function layoutCss(
    sliderId: string,
    slidesToShow: number,
    responsive: ResponsiveBreakpoint[],
    slideCount?: number,
): string {
    const root = `[data-slider="${sliderId}"]`;
    const band = (n: number) => {
        const width = `${root} > .slick-list > .slick-track > .slick-slide{flex:0 0 ${100 / n}%}`;
        const arrows = slideCount === undefined ? '' : `${root} > button{opacity:${slideCount <= n ? 0.2 : 1}}`;
        return width + arrows;
    };
    const mediaRules = [...responsive]
        .sort((a, b) => b.breakpoint - a.breakpoint)
        .map((b) => `@media (max-width:${b.breakpoint}px){${band(b.settings.slidesToShow ?? slidesToShow)}}`);
    return [band(slidesToShow), ...mediaRules].join('');
}

const Slider: React.FC<SliderProps> = ({
    infinite = false,
    slidesToShow = 1,
    slidesToScroll = 1,
    initialSlide = 0,
    dots = false,
    swipe = true,
    rows = 1,
    responsive = [],
    nextArrow,
    prevArrow,
    className,
    children,
}) => {
    const sliderId = useId();
    const activeResp = useResponsiveSettings(responsive);

    const effectiveSlidesToShow = activeResp?.settings.slidesToShow ?? slidesToShow;
    const effectiveSlidesToScroll = activeResp?.settings.slidesToScroll ?? slidesToScroll;
    const effectiveSwipe = activeResp?.settings.swipe ?? swipe;

    const [emblaRef, emblaApi] = useEmblaCarousel({
        loop: infinite,
        startIndex: initialSlide,
        slidesToScroll: effectiveSlidesToScroll,
        watchDrag: effectiveSwipe,
        align: 'start',
        containScroll: 'trimSnaps',
    });

    const groups = useMemo(() => {
        const all = Children.toArray(children);
        if (rows <= 1) return all.map((child) => [child]);
        const chunked: React.ReactNode[][] = [];
        for (let i = 0; i < all.length; i += rows) {
            chunked.push(all.slice(i, i + rows));
        }
        return chunked;
    }, [children, rows]);

    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(true);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

    useEffect(() => {
        if (!emblaApi) return;
        const onSelect = () => {
            setCanScrollPrev(emblaApi.canScrollPrev());
            setCanScrollNext(emblaApi.canScrollNext());
            setSelectedIndex(emblaApi.selectedScrollSnap());
        };
        const onReInit = () => {
            setScrollSnaps(emblaApi.scrollSnapList());
            onSelect();
        };
        onReInit();
        emblaApi.on('select', onSelect);
        emblaApi.on('reInit', onReInit);
        return () => {
            emblaApi.off('select', onSelect);
            emblaApi.off('reInit', onReInit);
        };
    }, [emblaApi]);

    // Re-init when the visible-slide count changes via media query so the
    // new flex-basis is picked up by embla's internal measurements.
    useEffect(() => {
        emblaApi?.reInit();
    }, [emblaApi, effectiveSlidesToShow, effectiveSlidesToScroll]);

    const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
    const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
    const scrollTo = useCallback((i: number) => emblaApi?.scrollTo(i), [emblaApi]);

    const renderArrow = (arrow: React.ReactElement | undefined, disabled: boolean, onClick: () => void) => {
        if (!isValidElement(arrow)) return null;
        const typedArrow = arrow as React.ReactElement<{ onClick?: () => void; className?: string }>;
        const cn = [typedArrow.props.className, disabled ? 'slick-disabled' : ''].filter(Boolean).join(' ');
        return cloneElement(typedArrow, { onClick, className: cn });
    };

    const rootClass = ['slick-slider', className].filter(Boolean).join(' ');
    const hasArrows = isValidElement(prevArrow) || isValidElement(nextArrow);

    return (
        <div className={rootClass} data-slider={sliderId}>
            <style>
                {layoutCss(sliderId, slidesToShow, responsive, infinite || !hasArrows ? undefined : groups.length)}
            </style>
            {renderArrow(prevArrow, !canScrollPrev && !infinite, scrollPrev)}
            <div className="slick-list" ref={emblaRef} style={{ overflow: 'hidden' }}>
                <div className="slick-track" style={{ display: 'flex' }}>
                    {groups.map((group, i) => {
                        const slideClass = ['slick-slide', i === selectedIndex ? 'slick-active' : '']
                            .filter(Boolean)
                            .join(' ');
                        return (
                            <div
                                key={i}
                                className={slideClass}
                                style={{ minWidth: 0, height: 'auto', alignSelf: 'stretch' }}
                            >
                                <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    {group.length === 1
                                        ? group[0]
                                        : group.map((child, j) => (
                                              <div key={j} style={{ height: '100%' }}>
                                                  {child}
                                              </div>
                                          ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            {renderArrow(nextArrow, !canScrollNext && !infinite, scrollNext)}
            {dots && scrollSnaps.length > 1 && (
                <ul className="slick-dots">
                    {scrollSnaps.map((_, i) => (
                        <li key={i} className={i === selectedIndex ? 'slick-active' : ''}>
                            <button type="button" onClick={() => scrollTo(i)} aria-label={`Go to slide ${i + 1}`} />
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default Slider;
